import { alert } from '@/services/alert';
import { trackMessageDraftAbandoned } from '@/services/analytics';
import { fetchBot } from '@/services/services';
import { SMS_MAX_MESSAGE_LENGTH } from '@bariendo/lib/constants';
import { getPatientsByPhone } from '@bariendo/lib/phone-catalog';
import { formatPhoneNumber } from '@bariendo/lib/phone-validations';
import { isReferenceOf } from '@bariendo/lib/services';
import { Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { getReferenceString, resolveId } from '@medplum/core';
import { Organization, Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { usePostHog } from 'posthog-js/react';
import { ReactElement, useEffect, useState } from 'react';
import type { SendSmsParams } from '../../../bots/src/handlers/send-sms-message';

/** Who a text from a log row goes to. */
export interface SmsTarget {
  phoneNumber: string;
  /**
   * The patient or partner the row is with, when known. An Organization pins the text to
   * that partner. A Patient only picks the from-number: `send-sms-message` files a text
   * against whoever holds the number today (every patient who does, or nobody), so the modal
   * checks the number is still on this patient's record and warns when it is not.
   */
  recipient?: Reference<Patient | Organization>;
  /** Titles the modal when we know whose number it is. */
  displayName?: string;
}

export interface SendSmsModalProps {
  target: SmsTarget | undefined;
  opened: boolean;
  onClose: () => void;
}

/**
 * Sends one text to a number, from anywhere that has a number and no timeline.
 *
 * The timeline message box reads its recipient from the timeline it sits in, so a number
 * that is neither a patient nor a partner has nowhere to be texted from. This takes the
 * number as a prop and hands it to the same `send-sms-message` bot, which picks the
 * from-number and, when nothing matches the number, writes the Communication with no
 * subject — so the log the text was sent from is also where the reply lands.
 *
 * Attachments are out of scope; the timeline box still does those.
 */
export function SendSmsModal({ target, opened, onClose }: SendSmsModalProps): ReactElement {
  const medplum = useMedplum();
  const posthog = usePostHog();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  // `false` once the lookup has shown the number is not on the named patient's record.
  const [numberOnRecord, setNumberOnRecord] = useState<boolean | undefined>();

  const phoneNumber = target?.phoneNumber;
  const patientReference = isReferenceOf(target?.recipient, 'Patient') ? target?.recipient?.reference : undefined;

  // A log row can carry a number the patient has since given up. The bot files by number, so
  // the reader is told before they text a stranger under this patient's name.
  useEffect(() => {
    setNumberOnRecord(undefined);
    if (!opened || !phoneNumber || !patientReference) {
      return;
    }
    let cancelled = false;
    getPatientsByPhone(medplum, phoneNumber)
      .then((patients) => {
        if (!cancelled) {
          setNumberOnRecord(patients.some((patient) => getReferenceString(patient) === patientReference));
        }
      })
      .catch(() => {
        // A failed lookup is no reason to block a text; it just cannot warn.
      });
    return () => {
      cancelled = true;
    };
  }, [medplum, opened, phoneNumber, patientReference]);

  const sizeLimitExceeded = message.length > SMS_MAX_MESSAGE_LENGTH;
  const canSend = Boolean(target) && message.trim().length > 0 && !sizeLimitExceeded && !isSending;

  // Every way out except a successful send comes through here, so a half-typed draft is
  // reported the way the timeline composer reports one, and a send in flight cannot be
  // dismissed from under itself.
  const handleClose = (): void => {
    if (isSending) {
      return;
    }
    if (message.trim()) {
      trackMessageDraftAbandoned(posthog, {
        message_type: 'sms',
        draft_length: message.length,
        had_attachments: false,
        patient_id: patientReference ? resolveId(target?.recipient) : undefined,
        organization_id: isReferenceOf(target?.recipient, 'Organization') ? resolveId(target?.recipient) : undefined,
      });
    }
    setMessage('');
    onClose();
  };

  const handleSend = async (): Promise<void> => {
    if (!target || !canSend) {
      return;
    }
    setIsSending(true);
    try {
      const input: SendSmsParams = { phoneNumber: target.phoneNumber, message };
      if (target.recipient) {
        input.recipient = getReferenceString(target.recipient);
      }
      await fetchBot(medplum, 'send-sms-message', input);
      alert.success('Text sent');
      setMessage('');
      onClose();
    } catch (error) {
      alert.error(`Couldn't send SMS: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSending(false);
    }
  };

  const number = formatPhoneNumber(target?.phoneNumber);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      closeOnEscape={!isSending}
      closeOnClickOutside={!isSending}
      withCloseButton={!isSending}
      title={`Text ${target?.displayName ?? number}`}
      centered
      size="sm"
    >
      <Stack gap="md">
        {target?.displayName && (
          <Text size="sm" c="dimmed">
            {number}
          </Text>
        )}
        {numberOnRecord === false && (
          <Text size="sm" c="orange.8">
            This number is not on {target?.displayName ?? 'the patient'}'s record.
          </Text>
        )}
        <Textarea
          data-autofocus
          placeholder="Write a message..."
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
          error={sizeLimitExceeded ? `Message exceeds ${SMS_MAX_MESSAGE_LENGTH} characters` : undefined}
          disabled={isSending}
          autosize
          minRows={3}
          maxRows={10}
          onKeyDown={(event) => {
            // An IME commits its candidate with Enter; that keystroke is not a send.
            if (event.nativeEvent.isComposing) {
              return;
            }
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void handleSend();
            }
          }}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={isSending}>
            Cancel
          </Button>
          <Button onClick={() => void handleSend()} loading={isSending} disabled={!canSend}>
            Send
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
